import {
  VStack,
  Heading,
  Text,
  SimpleGrid,
  GridItem,
  FormControl,
  FormLabel,
  Button,
  useBreakpointValue,
  NumberInput,
  NumberInputField,
  useColorModeValue,
  AlertDialogContent,
  AlertDialog,
  AlertDialogBody,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogCloseButton,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import DrawDateSelection from "../components/DrawDateSelection";
import axios from "axios";
import { useDrawDates } from "../hooks/useDrawDates";
import { generatePrizeGroup } from "../utils/generatePrizeGroup";
import { ComputeResultRequestBody, FetchResultRequestBody } from "./types";

const TotoNumberInput = ({
  setIsComputed,
  setScore,
  setPrizeMoney,
}: {
  setIsComputed: Dispatch<SetStateAction<any>>;
  setScore: Dispatch<SetStateAction<any>>;
  setPrizeMoney: Dispatch<SetStateAction<any>>;
}) => {
  const bgColor = useColorModeValue("gray.50", "whiteAlpha.50");
  const colSpan = useBreakpointValue({ base: 4, md: 2 });

  const [one, setOne] = useState("");
  const [two, setTwo] = useState("");
  const [three, setThree] = useState("");
  const [four, setFour] = useState("");
  const [five, setFive] = useState("");
  const [six, setSix] = useState("");

  const { drawDates, drawDays, isLoading } = useDrawDates();

  const [drawDate, setDrawDate] = useState("");

  // Set default draw date when data loads
  useEffect(() => {
    if (drawDates.length > 0 && !drawDate) {
      setDrawDate(drawDates[0]);
    }
  }, [drawDates, drawDate]);

  const placeholders = ["1", "2", "3", "4", "5", "6"];
  const setters = [setOne, setTwo, setThree, setFour, setFive, setSix];

  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const [isError, setIsError] = useState(false);

  const onClose = () => setIsError(false);

  const handleOnSubmit = async () => {
    const totoNumberInput: number[] = [
      parseInt(one),
      parseInt(two),
      parseInt(three),
      parseInt(four),
      parseInt(five),
      parseInt(six),
    ];

    if (totoNumberInput.includes(NaN)) {
      setIsError(true);
      return;
    }

    if (new Set(totoNumberInput).size !== totoNumberInput.length) {
      setIsError(true);
      return;
    }

    const computeResultRequestBody = {
      numbers: totoNumberInput,
      date: drawDate,
    } as ComputeResultRequestBody;

    const fetchResultRequestBody = {
      date: drawDate,
    } as FetchResultRequestBody;

    const endpoint = process.env.REACT_APP_ENDPOINT as string;

    const computePath = "/api/results/compute";

    const computeResponse = await axios.post(
      `${endpoint}${computePath}`,
      computeResultRequestBody
    );
    const { score } = computeResponse.data;

    const fetchResultPath = "/api/results";

    const fetchResultResponse = await axios.post(
      `${endpoint}${fetchResultPath}`,
      fetchResultRequestBody
    );

    const { prizes } = fetchResultResponse.data.Items[0];

    setScore(score);

    setPrizeMoney(prizes[generatePrizeGroup(score)]);
    setIsComputed(true);
  };

  return (
    <>
      <VStack
        w="full"
        h="full"
        p={10}
        spacing={10}
        alignItems="flex-start"
        bg={bgColor}
      >
        <VStack spacing={3} alignItems="flex-start">
          <Heading size="2xl">Did I Win TOTO?</Heading>
          <Text>Enter your number and select the draw date here!</Text>
        </VStack>
        <SimpleGrid columns={12} columnGap={3} rowGap={6} w="full">
          {placeholders.map((result, idx) => {
            return (
              <GridItem colSpan={colSpan}>
                <FormControl id={result}>
                  <NumberInput
                    size="lg"
                    min={1}
                    max={49}
                    onChange={(event) => setters[idx](event)}
                  >
                    <NumberInputField
                      fontSize="3xl"
                      textAlign="center"
                      placeholder={result}
                      padding="-0.5"
                    />
                  </NumberInput>
                </FormControl>
              </GridItem>
            );
          })}
          <GridItem colSpan={12}>
            <FormControl>
              <FormLabel>Draw Date</FormLabel>
              <DrawDateSelection
                drawDate={drawDate}
                setDrawDate={setDrawDate}
                allDrawDates={drawDates}
                allDrawDays={drawDays}
                isLoading={isLoading}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={12}>
            <Button onClick={handleOnSubmit} size="lg" w="full">
              Check if I won!
            </Button>
          </GridItem>
        </SimpleGrid>
      </VStack>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={isError}
        isCentered
      >
        <AlertDialogOverlay />

        <AlertDialogContent>
          <AlertDialogHeader>Invalid TOTO Input</AlertDialogHeader>
          <AlertDialogCloseButton onClick={onClose} />
          <AlertDialogBody>
            Do ensure you enter a valid set of TOTO numbers!
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button onClick={onClose} colorScheme="red" ml={3}>
              Back
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TotoNumberInput;
